import Decimal from 'decimal.js';

/**
 * 统一支付计算工具类
 * ================================
 *
 * 【重要】本文件与后端各 CardPaymentStrategy 的抵扣/补差计算逻辑需保持一致，
 * 凡改动计算规则（抵扣顺序、补差公式、票数/点数换算等）时，需前后端一起调整逻辑，避免展示与校验不一致。
 *
 * 【文件职责】
 * 所有福利卡相关的计算逻辑集中于此，Vue组件只负责UI交互。
 *
 * 【导出函数说明】
 * 1. calculateDeduction(selectedCards, orderInfo)
 *    - orderInfo: { orderAmount, productType, sortedSeatPrices }
 *    - 调用时机：用户选择/调整卡片后，实时计算抵扣结果
 *    - 职责：计算总抵扣、微信补差金额、是否可支付
 * 
 * 2. calculateInitialSelectVote(card, allCards, orderInfo, isChecking)
 *    - 调用时机：用户点击选中卡片时，计算该卡的初始票数
 *    - 职责：根据订单缺口智能分配初始 selectVote
 */

// ================= 常量定义 =================

// 卡点类型枚举
export const CardPointType = {
  POINTS_1_TO_1: '0',     // (0) 点数卡: 1:1
  MOVIE_TICKET: '1',      // (1) 通兑票
  MOVIE_1_TO_1_3: '2',    // (2) 1:1.3卡
  TIMES: '3',             // (3) 次卡
  GOLD_BEAN: '4'          // (4) 金豆卡
};

// 当前版本将“次卡/金豆卡”视为固定单位订单，这两类卡不走微信补差计算。
export function isFixedUnitCardType(cardType) {
  return cardType === CardPointType.TIMES || cardType === CardPointType.GOLD_BEAN;
}

const MSG_CONTINUE_SELECT = '已选福利卡余额不足，请继续选择';
const MSG_NO_REPAIR_INSUFFICIENT = '已选福利卡不支持补差价，余额不足';

/** 与 Java {@code setScale(2, RoundingMode.HALF_UP)} 一致 */
function toMoneyHalfUp2(d) {
  return d.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2);
}

/** 2 位 HALF_UP → number（顺序抵扣、totalDeduct、收银台 wxAmount 提交同一套） */
function halfUp2(v) {
  return decOf(v).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
}

/** 非负金额元字符串*/
function formatMoney(v) {
  return toMoneyHalfUp2(Decimal.max(0, decOf(v)));
}

function decOf(v) {
  if (v == null || v === '') {
    return new Decimal(0);
  }
  if (v instanceof Decimal) {
    return v;
  }
  return new Decimal(v);
}

function maxWxPointsFromAllCards(allCards) {
  let maxP = new Decimal(0);
  for (const c of allCards) {
    const raw = c.wxPoints;
    if (raw == null || raw === '') {
      continue;
    }
    const n = Number(raw);
    if (isNaN(n) || n <= 0) {
      continue;
    }
    const p = new Decimal(raw);
    if (p.gt(maxP)) {
      maxP = p;
    }
  }
  return Decimal.max(new Decimal(1), maxP);
}

function ceilDiv(numerator, denominator) {
  const a = decOf(numerator);
  const b = decOf(denominator);
  if (b.lte(0)) {
    return 0;
  }
  return a.div(b).ceil().toNumber();
}

// ================= 核心导出函数 =================

/**
 * [核心函数1] 计算选中卡片的抵扣详情和剩余微信支付金额
 * 改动需与后端策略校验逻辑同步，见文件头说明。
 *
 * @param {Array} selectedCards - 用户选中的卡片列表 (按选择顺序)
 * @param {Object} orderInfo - 订单信息 { orderAmount, productType, sortedSeatPrices, orderCardPointType }
 * @returns {Object} { totalDeduct, wxAmount, deductUnit, canPay, errorMsg }
 */
export function calculateDeduction(selectedCards, orderInfo, allCards = []) {
  // 未选卡时：使用福利卡列表中最大 wxPoints 计算补差价
  if (!selectedCards || selectedCards.length === 0) {
    // 无福利卡列表时不允许支付
    if (!allCards || allCards.length === 0) {
      return {
        totalDeduct: 0,
        wxAmount: '0.00',
        deductUnit: '点',
        canPay: false,
        errorMsg: '暂无可用福利卡，无法支付',
        remainingRequired: 0
      };
    }
    // 未选卡时优先使用订单口径判断是否允许补差，避免卡列表混入其他卡型导致误算补差金额。
    const firstCardType = orderInfo.orderCardPointType || allCards[0]?.cardPointType;
    const unit = allCards[0]?.cardPointTypeUnit || '点';
    if (isFixedUnitCardType(firstCardType)) {
      // 固定单位订单在未选卡时不预估微信补差，避免把“0.22金豆”误算成“0.22元补差”。
      return {
        totalDeduct: 0,
        wxAmount: '0.00',
        deductUnit: unit,
        canPay: true,
        errorMsg: '',
        remainingRequired: 0
      };
    }

    const maxWxPoints = maxWxPointsFromAllCards(allCards);
    const orderAmount = decOf(orderInfo.orderAmount);
    let wxDec;
    const sortedPrices = orderInfo.sortedSeatPrices || [];
    if (orderInfo.productType === '2' && sortedPrices.length > 0) {
      // 电影订单：0 票时全额座位价 × maxWxPoints
      wxDec = calcMovieWxAmount(sortedPrices, 0, 0).mul(maxWxPoints);
    } else {
      wxDec = orderAmount.mul(maxWxPoints);
    }
    return {
      totalDeduct: 0,
      wxAmount: formatMoney(wxDec),
      deductUnit: unit,
      canPay: true,
      errorMsg: '',
      remainingRequired: 0
    };
  }

  const firstCard = selectedCards[0];
  const cardType = firstCard.cardPointType;
  const unit = firstCard.cardPointTypeUnit || '点';
  const orderAmount = Number(orderInfo.orderAmount) || 0;

  // 电影订单通兑票：使用专门的计算函数
  if (cardType === CardPointType.MOVIE_TICKET && orderInfo.productType === '2') {
    return calcMovieTicketDeduction(selectedCards, orderInfo, allCards);
  }

  // 通兑票 + 非电影实物订单：使用专门的商品订单计算函数
  if (cardType === CardPointType.MOVIE_TICKET && orderInfo.productType !== '2') {
    return calcProductTicketDeduction(selectedCards, orderInfo, allCards);
  }

  // 其他卡类型：通用抵扣逻辑
  const { totalDeduct, remainingRequired } = calcSequentialDeduction(
    selectedCards, 
    Number(firstCard.requiredUnits), 
    cardType
  );

  // 计算微信支付金额
  let wxAmount = 0;
  let canPay = true;
  let errorMsg = '';

  if (remainingRequired > 0) {
    // 次卡/金豆卡即使 repairDiffPrice 为空，也按“不支持补差价”处理。
    const repairType = isFixedUnitCardType(cardType) ? '0' : (firstCard.repairDiffPrice || '0');
    
    if (repairType === '0') {
      canPay = false;
      errorMsg = '余额不足';
    } else {
      // 支持补差价
      wxAmount = calcWxAmount(cardType, remainingRequired, firstCard, orderInfo);
      if (wxAmount < 0) {
        canPay = false;
        errorMsg = '通兑票数据异常';
      }
    }
  }

  return {
    totalDeduct: halfUp2(totalDeduct),
    wxAmount: formatMoney(wxAmount),
    deductUnit: unit,
    canPay,
    errorMsg,
    remainingRequired: remainingRequired > 0 ? (unit === '票' ? Math.ceil(remainingRequired) : halfUp2(remainingRequired)) : 0
  };
}

/**
 * [核心函数2] 计算卡片选中时的初始 selectVote 值
 * 
 * @param {Object} card - 当前选中的卡片
 * @param {Array} allCards - 所有卡片列表
 * @param {Object} orderInfo - 订单信息 { productType }
 * @param {boolean} isChecking - 是选中(true)还是取消选中(false)
 * @returns {number} 该卡片的初始 selectVote 值
 */
export function calculateInitialSelectVote(card, allCards, orderInfo, isChecking) {
  if (!isChecking) return 0;

  const cardType = card.cardPointType;
  if (cardType !== CardPointType.MOVIE_TICKET) return 1;
  
  // 电影或非电影订单通兑票统一逻辑：填补订单缺口
  const otherTotal = allCards
    .filter(c => c.check && c !== card && c.cardPointType === CardPointType.MOVIE_TICKET)
    .reduce((sum, c) => sum.plus(decOf(c.selectVote)), new Decimal(0));

  const orderNeededRaw = decOf(card.requiredUnits);
  const orderNeeded = orderNeededRaw.gt(0) ? orderNeededRaw : new Decimal(1);
  const stillNeeded = Decimal.max(0, orderNeeded.sub(otherTotal));
  const cardBalance = decOf(card.balance);

  // 初始值为 min(余额, 仍需票数)
  return Decimal.max(0, Decimal.min(cardBalance, stillNeeded)).toNumber();
}

// ================= 内部计算函数 =================

/** 同组是否还有未勾选且余额>0 的卡 */
function hasUnselectedSameGroupBalance(firstCard, allCards) {
  if (!firstCard || !Array.isArray(allCards) || allCards.length === 0) {
    return false;
  }
  const payGroup = firstCard.payGroup;
  return allCards.some(c => !c.check && c.payGroup === payGroup && Number(c.balance || 0) > 0);
}

/** 同组还有未选卡，或已选卡尚未用尽余额，需要继续选择 */
function shouldContinueSelectSameGroup(firstCard, allCards, usedTickets) {
  if (hasUnselectedSameGroupBalance(firstCard, allCards)) {
    return true;
  }
  if (!firstCard || !Array.isArray(allCards) || allCards.length === 0) {
    return false;
  }
  const payGroup = firstCard.payGroup;
  const totalGroupBalance = allCards
    .filter(c => c.payGroup === payGroup)
    .reduce((sum, c) => sum + (Number(c.balance) || 0), 0);
  return decOf(usedTickets).toNumber() < totalGroupBalance;
}

/**
 * 顺序抵扣计算（通用）
 * 
 * 【核心逻辑】按选中顺序"先选先抵"，每张卡用 减法 计算剩余待抵量：
 *   remainingRequired = 上一步剩余 - 本卡实际抵扣
 * 避免每张卡都重新公式计算，且每次减法后四舍五入到2位小数，消除 JS 浮点精度误差
 * （如 43.9 - 16.64 在 JS 中为 27.259999999999998， rounding 后为 27.26）
 */
function calcSequentialDeduction(selectedCards, orderRequiredUnits, cardType) {
  // 使用 halfUp2 避免浮点链式运算误差
  let remainingRequired = halfUp2(decOf(orderRequiredUnits));
  let totalDeduct = 0;

  for (const card of selectedCards) {
    if (remainingRequired <= 0) {
      card.realDeduct = 0;
      continue;
    }

    const cardAvailable = cardType === CardPointType.MOVIE_TICKET
      ? decOf(card.selectVote)
      : decOf(card.balance);

    const actualDeduct = Decimal.min(cardAvailable, decOf(remainingRequired));
    const actualDeductRounded = halfUp2(actualDeduct);
    card.realDeduct = actualDeductRounded;
    totalDeduct += actualDeductRounded;
    remainingRequired = halfUp2(decOf(remainingRequired).sub(decOf(actualDeductRounded)));
  }

  return { totalDeduct, remainingRequired };
}

/**
 * 计算非电影通兑票场景的微信补差金额
 */
function calcWxAmount(cardType, remainingRequired, firstCard, _orderInfo) {
  const rem = decOf(remainingRequired);
  const wxP = decOf(firstCard.wxPoints != null && firstCard.wxPoints !== '' ? firstCard.wxPoints : 1);

  switch (cardType) {
    case CardPointType.POINTS_1_TO_1:
    case CardPointType.MOVIE_1_TO_1_3:
      return rem.mul(wxP).toNumber();

    case CardPointType.MOVIE_TICKET:
      // 商品品订单通兑票
      const upgradeNum = decOf(firstCard.upgradeCardNum);
      if (upgradeNum.gt(0)) {
        return rem.mul(upgradeNum).mul(wxP).toNumber();
      }
      return -1; // 异常

    case CardPointType.TIMES:
      return -1; // 不支持补差价

    case CardPointType.GOLD_BEAN:
      return -1; // 不支持补差价

    default:
      return -1;
  }
}

/**
 * 通兑票 + 非电影实物订单抵扣/补差计算
 *
 * 业务规则：
 * - 所有通兑票（不支持/普通/风险补差）都先按用户选择的票数计算可抵扣的最大金额：
 *     maxDeductAmount = usedTickets × upgradeCardNum
 * - 剩余金额 = 订单总额 - maxDeductAmount（最小为 0）
 * - 不支持补差价(repairType=0)：若剩余金额>0，同组还有未用余额则提示继续选择，否则提示不支持补差价
 * - 普通补差价(repairType=1) & 风险补差价(repairType=2)：剩余金额 × wxPoints 作为微信补差
 *
 * @param {Array} selectedCards - 选中的通兑票卡片（按选择顺序）
 * @param {Object} orderInfo - 订单信息 { orderAmount }
 * @param {Array} allCards - 当前订单下所有可用福利卡列表（用于同组校验）
 * @returns {Object} { totalDeduct, wxAmount, deductUnit, canPay, errorMsg, remainingRequired }
 */
function calcProductTicketDeduction(selectedCards, orderInfo, allCards = []) {
  const firstCard = selectedCards[0];
  const orderAmount = decOf(orderInfo.orderAmount);
  const upgradeNum = decOf(firstCard.upgradeCardNum);

  if (upgradeNum.lte(0)) {
    return {
      totalDeduct: 0,
      wxAmount: formatMoney(0),
      deductUnit: firstCard.cardPointTypeUnit || '点',
      canPay: false,
      errorMsg: '通兑票配置异常',
      remainingRequired: 0
    };
  }

  // 使用顺序抵扣，只是为了给每张卡写入 realDeduct（使用票数），并得到总使用票数
  const { totalDeduct } = calcSequentialDeduction(
    selectedCards,
    Number(firstCard.requiredUnits),
    CardPointType.MOVIE_TICKET
  );

  const usedTickets = decOf(totalDeduct);
  const wxPoints = decOf(firstCard.wxPoints != null && firstCard.wxPoints !== '' ? firstCard.wxPoints : 1);

  // 剩余应付金额（与 orderAmount 同单位）
  let remainingAmount = orderAmount.sub(usedTickets.mul(upgradeNum));
  if (remainingAmount.lte(0)) {
    return {
      totalDeduct: halfUp2(usedTickets),
      wxAmount: formatMoney(0),
      deductUnit: '票',
      canPay: true,
      errorMsg: '',
      remainingRequired: 0
    };
  }

  const repairType = firstCard.repairDiffPrice || '0';

  // 不支持补差价：允许勾选，票/金额不够则不能确认
  if (repairType === '0') {
    const continueSelect = shouldContinueSelectSameGroup(firstCard, allCards, usedTickets);
    return {
      totalDeduct: halfUp2(usedTickets),
      wxAmount: formatMoney(0),
      deductUnit: '票',
      canPay: false,
      errorMsg: continueSelect ? MSG_CONTINUE_SELECT : MSG_NO_REPAIR_INSUFFICIENT,
      remainingRequired: ceilDiv(remainingAmount, upgradeNum)
    };
  }

  // 普通补差价：必须同组全部卡选尽并用完余额后，剩余金额才能走补差
  if (repairType === '1' && remainingAmount.gt(0) && shouldContinueSelectSameGroup(firstCard, allCards, usedTickets)) {
    return {
      totalDeduct: halfUp2(usedTickets),
      wxAmount: formatMoney(0),
      deductUnit: '票',
      canPay: false,
      errorMsg: MSG_CONTINUE_SELECT,
      remainingRequired: ceilDiv(remainingAmount, upgradeNum)
    };
  }

  // 普通补差价 & 风险补差价：精确补差 = 剩余金额 × 微信汇率
  const wxAmountDec = remainingAmount.mul(wxPoints);

  return {
    totalDeduct: halfUp2(usedTickets),
    wxAmount: formatMoney(wxAmountDec),
    deductUnit: '票',
    canPay: true,
    errorMsg: '',
    remainingRequired: ceilDiv(remainingAmount, upgradeNum)
  };
}

/**
 * 电影订单通兑票抵扣计算（三种补差价规则）
 * 改动需与后端 MovieTicketStrategy 等逻辑同步，见文件头说明。
 *
 * 【核心算法】
 * 每个座位优先1票抵扣，1票对应面值与座位售价差额汇总后，计算需要的总票数。
 * 
 * 【所需票数计算公式】
 * minRequiredTickets = ceil(座位数 + 总差额 / 票面值)
 * 例：3个座位30/40/50元，票面值20元
 *     差额 = (30-20) + (40-20) + (50-20) = 60
 *     所需票数 = 3 + ceil(60/20) = 3 + 3 = 6票
 * 
 * 【三种补差价类型】
 * 
 * 1. 不支持补差价 (repairType=0)
 *    - 卡上有余额即可勾选，必须选择足够票数才能确认支付
 *    - 示例：3座位(30/40/50)，面值20，需6票
 *    - 有6票：可支付，无需补差
 *    - 有5票且同组还有未选卡：不可支付，提示"已选福利卡余额不足，请继续选择"
 *    - 有5票且同组已选尽：不可支付，提示"已选福利卡不支持补差价，余额不足"
 * 
 * 2. 普通补差价 (repairType=1)
 *    - 必须选择足够票数（优先用票），不足时需继续选择同组卡
 *    - 只有当同组所有卡都用完还不够时，才允许微信补差
 *    - 示例：3座位(30/40/50)，面值20，需6票
 *    - 有6票：可支付，补0元
 *    - 有4票：每座抵1票后，总差额60 - 多余1票×20 = 40元，需补40元
 *    - 有2票：需要提示继续选择（如有同组卡）或全用完后补差
 * 
 * 3. 风险补差价 (repairType=2)
 *    - 用户可自由选择票数（步进器可调），不足部分微信补差
 *    - 示例：3座位(30/40/50)，面值20，需6票
 *    - 选1票：最低价30抵扣1票后，差额10+40+50=100元，需补100元
 *    - 选3票：分别抵扣1座位后，差额10+20+30=60元，需补60元
 *    - 选4票：分别抵1座后，总差额60-多余1票×20=40元，需补40元
 * 
 * @param {Array} selectedCards - 用户选中的卡片列表（按选择顺序）
 * @param {Object} orderInfo - 订单信息，包含 sortedSeatPrices（从低到高排序的座位价格）
 * @returns {Object} { totalDeduct, wxAmount, deductUnit, canPay, errorMsg, allowAdjustTicketCount }
 */
function calcMovieTicketDeduction(selectedCards, orderInfo, allCards = []) {
  const firstCard = selectedCards[0];
  const limit = Number(firstCard.movieAmountLimit) || 0;
  const repairType = firstCard.repairDiffPrice || '0';
  
  // 使用后端返回的排序价格（从低到高）
  const sortedPrices = orderInfo.sortedSeatPrices || [];
  
  // 使用后端返回的所需最少票数
  const minRequiredTickets = Number(firstCard.requiredUnits) || 0;
  
  // 计算总票数
  const totalTickets = selectedCards.reduce((sum, c) => 
    sum + (Number(c.selectVote) || 0), 0);

  // 按顺序分摊 realDeduct
  let remaining = minRequiredTickets;
  for (const card of selectedCards) {
    const vote = Number(card.selectVote) || 0;
    const deduct = Math.min(vote, remaining);
    card.realDeduct = deduct;
    remaining -= deduct;
  }

  const shortfallTickets = Math.max(0, minRequiredTickets - totalTickets);

  // 不支持补差价（repairType=0）：允许勾选，票数不够则不能确认
  if (repairType === '0') {
    if (totalTickets < minRequiredTickets) {
      const continueSelect = shouldContinueSelectSameGroup(firstCard, allCards, totalTickets);
      return { 
        totalDeduct: totalTickets, 
        wxAmount: '0.00', 
        deductUnit: '票', 
        canPay: false, 
        errorMsg: continueSelect ? MSG_CONTINUE_SELECT : MSG_NO_REPAIR_INSUFFICIENT,
        remainingRequired: shortfallTickets
      };
    }
    return { totalDeduct: totalTickets, wxAmount: '0.00', deductUnit: '票', canPay: true, errorMsg: '', remainingRequired: 0 };
  }

  // 普通补差价（repairType=1）：必须选择足够票数（>= minRequiredTickets）
  if (repairType === '1') {
    if (totalTickets < minRequiredTickets) {
      if (shouldContinueSelectSameGroup(firstCard, allCards, totalTickets)) {
        return { 
          totalDeduct: totalTickets, 
          wxAmount: '0.00', 
          deductUnit: '票',
          canPay: false, 
          errorMsg: MSG_CONTINUE_SELECT,
          remainingRequired: shortfallTickets
        };
      }
      // 同组余额已用完，允许微信补差（继续往下计算）
    }
  }

  // 普通/风险补差价：计算微信金额
  const wn = Number(firstCard.wxPoints);
  const wxPoints = !isNaN(wn) && wn > 0 ? decOf(firstCard.wxPoints) : new Decimal(1);
  const wxDec = calcMovieWxAmount(sortedPrices, totalTickets, limit).mul(wxPoints);

  return {
    totalDeduct: totalTickets,
    wxAmount: formatMoney(wxDec),
    deductUnit: '票',
    canPay: true,
    errorMsg: '',
    allowAdjustTicketCount: repairType === '2',
    remainingRequired: shortfallTickets
  };
}

/**
 * 计算电影订单微信补差金额（普通/风险补差价通用）
 * 
 * 【算法逻辑】
 * 座位价格从低到高排序后，按顺序用票抵扣：
 * 1. 每个座位优先使用1票抵扣（最多抵扣座位数张票）
 * 2. 多余的票（超出座位数的部分）转换为"额外价值"(extraValue)，用于填补差额
 * 3. 对于有票抵扣的座位：如果座位价 > 票面值，产生差额
 *    - 先用额外价值填补差额
 *    - 额外价值用完后，剩余差额计入微信补差
 * 4. 对于没有票抵扣的座位：整个座位价格计入微信补差
 * 
 * 【示例】3座位(30/40/50)，面值20
 * - 选6票：useTickets=3, extraValue=60。座位全抵扣，差额(10+20+30)=60由extraValue填补，wxAmount=0
 * - 选4票：useTickets=3, extraValue=20。差额60，extraValue填补20后，wxAmount=40
 * - 选3票：useTickets=3, extraValue=0。差额60无法填补，wxAmount=60
 * - 选2票：useTickets=2(抵扣30/40座位)。50座位无票，wxAmount=50+(40-20)=70? 
 *          实际：30抵1票差额10，40抵1票差额20，50无票=50，wxAmount=10+20+50=80
 * - 选1票：useTickets=1(抵扣最低30座位)。40/50无票，wxAmount=10+40+50=100
 * 
 * @param {Array} sortedPrices - 从低到高排序的座位价格列表
 * @param {number} totalTickets - 用户选择使用的总票数
 * @param {number} limit - 票面值（每票可抵扣的最大金额）
 * @returns {number} 需要微信补差的金额
 */
function calcMovieWxAmount(sortedPrices, totalTickets, limit) {
  if (!sortedPrices || sortedPrices.length === 0) {
    return new Decimal(0);
  }
  const seatCount = sortedPrices.length;

  const tickets = decOf(totalTickets != null ? totalTickets : 0);
  const lim = decOf(limit != null ? limit : 0);
  // 实际用于座位抵扣的票数（最多等于座位数）
  const useTickets = Math.min(Math.max(Math.trunc(tickets.toNumber()), 0), seatCount);

  // 多余票的额外价值（可用于填补差额）
  // 例：6票抵3座位，多3票 × 面值20 = 60额外价值
  let extraValue = tickets.sub(new Decimal(useTickets)).mul(lim);

  let wxAmount = new Decimal(0);

  // 从低价座位开始遍历（优先抵扣低价座位，差额小）
  for (let i = 0; i < seatCount; i++) {
    const price = decOf(sortedPrices[i]);

    if (i < useTickets) {
      // 该座位有票抵扣
      if (price.gt(lim)) {
        // 座位价超出票面值，产生差额
        const diff = price.sub(lim);
        if (extraValue.gte(diff)) {
          // 额外价值足够填补差额
          extraValue = extraValue.sub(diff);
        } else {
          // 额外价值不够，剩余差额需微信补
          wxAmount = wxAmount.add(diff.sub(extraValue));
          extraValue = new Decimal(0);
        }
      }
      // 座位价 <= 票面值：无差额，不需补差
    } else {
      // 该座位没有票抵扣，全额需微信补
      wxAmount = wxAmount.add(price);
    }
  }

  return wxAmount;
}

export { halfUp2 as wxAmountToPayNumber };
